// @flow

import styled from '@emotion/styled'
import { Link, graphql, useStaticQuery } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import * as React from 'react'

import { colors, fonts, media } from '@toggl/style'
import {
  Button,
  Card,
  Hero,
  Responsive,
  SectionBlock,
  SectionIceCream,
  SectionMobileNoBleedBlock,
  SectionModalVideo,
  text,
} from '@toggl/ui'
import { useAssets } from 'components'
import { LayoutTrack } from 'components/layouts'
import { TrackDownloads } from 'components/products'
import Signup from 'components/Signup'
import SignupFormFullWidth from 'components/SignupFormFullWidth'

import typeof Features from './features.yml'

const links = [
  {
    id: 'time-tracking',
    title: 'Time Tracking',
  },
  { id: 'reporting', title: 'Reporting' },
  { id: 'integrations', title: 'Integrations' },
  { id: 'project-management', title: 'Project & Revenue Tracking' },
  {
    id: 'team-management',
    title: 'Team Scheduling & Management',
  },
]

const ContactSalesPoints = [
  {
    text: (
      <>
        <text.Strong>Cross-platform apps</text.Strong> for all time tracking
        preferences.
      </>
    ),
  },
  {
    text: (
      <>
        <text.Strong>GDPR-compliant.</text.Strong> Your security is our
        priority.
      </>
    ),
  },
  {
    text: (
      <>
        <text.Strong>Top-rated support</text.Strong> with a response time of 3
        hours!
      </>
    ),
  },
  {
    text: (
      <>
        <text.Strong>99% uptime</text.Strong> for your peace of mind.
      </>
    ),
  },
]

const TimeTracking = () => {
  const [selectedTab, selectTab] = React.useState<
    'calendar' | 'offline' | 'apps'
  >('calendar')
  const activateCalendar = React.useCallback(() => selectTab('calendar'), [])
  const activateOffline = React.useCallback(() => selectTab('offline'), [])
  const activateApps = React.useCallback(() => selectTab('apps'), [])

  return (
    <FeatureBig>
      <Tabs>
        <Tab active={selectedTab === 'calendar'} onClick={activateCalendar}>
          <text.H5>Calendar view</text.H5>
          <text.P3>
            View your time entries in a calendar format that integrates with
            your Google and Outlook calendars.
          </text.P3>
        </Tab>
        <Tab active={selectedTab === 'offline'} onClick={activateOffline}>
          <text.H5>Offline tracking</text.H5>
          <text.P3>
            Track time in real time or track offline and sync later. Switch
            between timer mode and manual mode to track as you go or add time
            later.
          </text.P3>
        </Tab>
        <Tab active={selectedTab === 'apps'} onClick={activateApps}>
          <text.H5>Mobile and desktop apps</text.H5>
          <text.P3>
            Mobile apps for time tracking while on the move. Desktop apps for a
            personalized, automated time tracking workflow that minimizes
            distractions.
          </text.P3>
        </Tab>
      </Tabs>

      <TabImage>
        {selectedTab === 'calendar' && (
          <StaticImage
            formats={['auto', 'webp', 'avif']}
            src={'./assets/mockup-calendar.png'}
            alt={"Toggl Track's calendar view"}
            height={570}
            placeholder="none"
            objectFit="contain"
            layout="fixed"
          />
        )}

        {selectedTab === 'offline' && (
          <StaticImage
            formats={['auto', 'webp', 'avif']}
            src={'./assets/mockup-offline.png'}
            alt={"Toggl Track's list view with offline tracking featured"}
            height={570}
            placeholder="none"
            objectFit="contain"
            layout="fixed"
          />
        )}

        {selectedTab === 'apps' && (
          <StaticImage
            formats={['auto', 'webp', 'avif']}
            src={'./assets/mockup-devices.png'}
            alt={'Showcasing Toggl Track mobile, desktop and Apple Watch apps'}
            height={570}
            placeholder="none"
            objectFit="contain"
            layout="fixed"
          />
        )}
      </TabImage>
    </FeatureBig>
  )
}

const Reporting = () => {
  const [selectedTab, selectTab] = React.useState<'reports' | 'savedReports'>(
    'reports'
  )
  const activateReports = React.useCallback(() => selectTab('reports'), [])
  const activateSavedReports = React.useCallback(
    () => selectTab('savedReports'),
    []
  )

  return (
    <FeatureBig>
      <Tabs>
        <Tab active={selectedTab === 'reports'} onClick={activateReports}>
          <text.H5>Summary, Detailed, and Weekly Reports</text.H5>
          <text.P3>
            Select the level of detail you want to see, filter and sort your
            data, and create client-ready visuals and reports exportable via
            CSV, PDF or Excel.
          </text.P3>
        </Tab>
        <Tab
          active={selectedTab === 'savedReports'}
          onClick={activateSavedReports}
        >
          <text.H5>Saved Reports</text.H5>
          <text.P3>
            Create a dynamic permalink for specific reports and share the link
            with clients so they can track your work live, or bookmark for easy
            access.
          </text.P3>
        </Tab>
      </Tabs>

      <TabImage>
        {selectedTab === 'reports' && (
          <StaticImage
            formats={['auto', 'webp', 'avif']}
            src={'./assets/mockup-reports.png'}
            alt={
              "Toggl Track's summary reports with graphs and list of time entries sorted by projects and users"
            }
            height={570}
            placeholder="none"
            objectFit="contain"
            layout="fixed"
          />
        )}

        {selectedTab === 'savedReports' && (
          <StaticImage
            formats={['auto', 'webp', 'avif']}
            src={'./assets/mockup-saved.png'}
            alt={
              "Toggl Track's saved reports option that allows public or private link sharing"
            }
            height={570}
            placeholder="none"
            objectFit="contain"
            layout="fixed"
          />
        )}
      </TabImage>
    </FeatureBig>
  )
}

const ProjectManagement = () => {
  const [selectedTab, selectTab] = React.useState<'dashboard' | 'tasks'>(
    'dashboard'
  )
  const activateDashboard = React.useCallback(() => selectTab('dashboard'), [])
  const activateOffline = React.useCallback(() => selectTab('tasks'), [])

  return (
    <FeatureBig>
      <Tabs>
        <Tab active={selectedTab === 'dashboard'} onClick={activateDashboard}>
          <text.H5>Project Dashboard</text.H5>
          <text.P3>
            Forecast timelines, budgets, and track progress as you go. Spot
            problems early and fight off scope creep with easy-to-read visual
            data.
          </text.P3>
        </Tab>
        <Tab active={selectedTab === 'tasks'} onClick={activateOffline}>
          <text.H5>Tasks (sub-projects)</text.H5>
          <text.P3>
            Add an additional layer of activity management below Projects,
            allowing teams to assign workloads and create precise reports.
          </text.P3>
        </Tab>
      </Tabs>

      <TabImage>
        {selectedTab === 'dashboard' && (
          <StaticImage
            formats={['auto', 'webp', 'avif']}
            src={'./assets/mockup-project-dashboard.png'}
            alt={
              "Toggl Track's project dashboard showcasing project profitability based on billable rates"
            }
            height={570}
            placeholder="none"
            objectFit="contain"
            layout="fixed"
          />
        )}

        {selectedTab === 'tasks' && (
          <StaticImage
            formats={['auto', 'webp', 'avif']}
            src={'./assets/mockup-tasks.png'}
            alt={
              'Toggl Track allows tasks, which adds another hierarchy of data for projects'
            }
            height={570}
            placeholder="none"
            objectFit="contain"
            layout="fixed"
          />
        )}
      </TabImage>
    </FeatureBig>
  )
}

const TeamManagement = () => {
  const [selectedTab, selectTab] = React.useState<'userGroups' | 'audits'>(
    'userGroups'
  )
  const activateUserGroups = React.useCallback(
    () => selectTab('userGroups'),
    []
  )
  const activateAudits = React.useCallback(() => selectTab('audits'), [])

  return (
    <FeatureBig>
      <Tabs>
        <Tab active={selectedTab === 'userGroups'} onClick={activateUserGroups}>
          <text.H5>User groups</text.H5>
          <text.P3>
            Assign team members to user groups to give the same project
            permissions to everyone at once.
          </text.P3>
        </Tab>
        <Tab active={selectedTab === 'audits'} onClick={activateAudits}>
          <text.H5>Team member audits</text.H5>
          <text.P3>
            Use filters to quickly find users who haven’t tracked time — or
            tracked too little or too much.
          </text.P3>
        </Tab>
      </Tabs>

      <TabImage>
        {selectedTab === 'userGroups' && (
          <StaticImage
            formats={['auto', 'webp', 'avif']}
            src={'./assets/mockup-user-groups.png'}
            alt={
              "Toggl Track's user group feature allows grouping of team members to simplify assigning billable rates, user costs, and tasks."
            }
            height={570}
            placeholder="none"
            objectFit="contain"
            layout="fixed"
          />
        )}

        {selectedTab === 'audits' && (
          <StaticImage
            formats={['auto', 'webp', 'avif']}
            src={'./assets/mockup-team-audits.png'}
            alt={
              'Toggl Track allows audits for reports, that helps you filter out suspicious or incorrect entries, such as time entries which are short than 30 seconds.'
            }
            height={570}
            placeholder="none"
            objectFit="contain"
            layout="fixed"
          />
        )}
      </TabImage>
    </FeatureBig>
  )
}

const FeaturesComponent = ({ data, id, uploads }) => (
  <FeaturesWrapper id={id}>
    {data.items.map((feature, index) => (
      <Card.FeatureIcon
        key={index}
        title={feature.title}
        paragraph={feature.description}
        image={
          <img
            src={uploads?.[index]}
            alt={feature.title}
            loading="lazy"
            width="50"
          />
        }
        icon
      />
    ))}
  </FeaturesWrapper>
)

const FeaturesPage = () => {
  const { featuresYaml } = useStaticQuery(graphql`
    {
      featuresYaml {
        features {
          heading
          items {
            description
            image
            title
          }
          subheading
        }
      }
    }
  `)

  const { features } = (featuresYaml: Features)
  const uploads = useAssets(
    Object.fromEntries(
      features.map((f, index) => [
        `feature${index}`,
        f.items.map((i) => i.image),
      ])
    )
  )

  return (
    <LayoutTrack
      title={'Features | Toggl Track'}
      description={
        'Toggl Track will help you track your time, manage projects, coordinate with your team, and see your profitability - all backed up by powerful reporting'
      }
      ogImage={require('assets/toggl-track-features.jpg').default}
      showRatingSchema
    >
      <Hero.Wrapper>
        <Hero.ContentWrapper>
          <SplitContent>
            <LeftContent>
              <text.H1 size="mini" color={colors.darkPurple30}>
                Toggl Track Features
              </text.H1>
              <text.H2 color={colors.almostWhite}>
                Team time tracking for{' '}
                <text.Rotalic color={colors.pink}>better work</text.Rotalic>,
                not overwork
              </text.H2>
              <text.P2 color={colors.almostWhite}>
                Join 70,000+ businesses and leave the time tracking to us — for
                better project and team management, client reporting, and more.
              </text.P2>
              <Button.Primary as={Link} to="/track/signup/">
                Sign up for free
              </Button.Primary>
            </LeftContent>
            <RightContent>
              <StyledSectionModalVideo
                cta={<VideoButton>Watch 1-minute intro</VideoButton>}
                placeholder={
                  <StaticImage
                    src="./assets/illo-human-point.png"
                    alt="Stylized illustration of Toggl Track's calendar view with a human pointing at a button"
                    width={900}
                    sizes="(max-width: 525px) 450px, 900px"
                    formats={['auto', 'webp', 'avif']}
                    placeholder="none"
                  />
                }
              >
                <iframe
                  src={'https://www.youtube.com/embed/y_xmxEE13Hg'}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                />
              </StyledSectionModalVideo>
            </RightContent>
          </SplitContent>
        </Hero.ContentWrapper>
      </Hero.Wrapper>

      <Navigation>
        <NavigationWrapper>
          <text.UppercaseOneLiner color={colors.almostWhite}>
            Jump to section:
          </text.UppercaseOneLiner>
          <BunchOfLinks>
            <a href="#time-tracking">Time tracking</a>
            <a href="#integrations">Integrations</a>
            <a href="#reporting">Reporting</a>
            <a href="#project-management">Project management</a>
            <a href="#team-management">Team management</a>
          </BunchOfLinks>
        </NavigationWrapper>
      </Navigation>

      <FeaturesSection id="time-tracking" background={colors.almostWhite}>
        <FeaturesSectionTitle>
          <text.UppercaseOneLiner color={colors.darkPurple70}>
            Time tracking
          </text.UppercaseOneLiner>
          <text.H2 color={colors.fadedPurple}>
            Easier and more accurate than timesheets
          </text.H2>
        </FeaturesSectionTitle>
        <TimeTracking />

        <Responsive.MobileOnly>
          <FeaturesWrapper>
            <Card.FeatureIcon
              title="Calendar view"
              paragraph="View your time entries in a calendar format that integrates with
            your Google and Outlook calendars."
              image={
                <StaticImage
                  formats={['auto', 'webp', 'avif']}
                  src={'./assets/mockup-calendar.png'}
                  alt={"Toggl Track's calendar view"}
                  imgStyle={{ width: '100%', height: 'auto' }}
                  placeholder="none"
                  objectFit="contain"
                />
              }
              icon
            />

            <Card.FeatureIcon
              title="Offline tracking"
              paragraph="Track time in real time or track offline and sync later. Switch
            between timer mode and manual mode to track as you go or add time
            later."
              image={
                <StaticImage
                  formats={['auto', 'webp', 'avif']}
                  src={'./assets/mockup-offline.png'}
                  alt={"Toggl Track's list view with offline tracking featured"}
                  imgStyle={{ width: '100%', height: 'auto' }}
                  placeholder="none"
                  objectFit="contain"
                />
              }
              icon
            />

            <Card.FeatureIcon
              title="Mobile and desktop apps"
              paragraph="Mobile apps for time tracking while on the move. Desktop apps for a
            personalized, automated time tracking workflow that minimizes
            distractions."
              image={
                <StaticImage
                  formats={['auto', 'webp', 'avif']}
                  src={'./assets/mockup-devices.png'}
                  alt={
                    'Showcasing Toggl Track mobile, desktop and Apple Watch apps'
                  }
                  imgStyle={{ width: '100%', height: 'auto' }}
                  placeholder="none"
                  objectFit="contain"
                />
              }
              icon
            />
          </FeaturesWrapper>
        </Responsive.MobileOnly>

        <FeaturesComponent
          id={links[0].id}
          data={features[0]}
          uploads={uploads.feature0}
        />
      </FeaturesSection>

      <FeaturesSection id="integrations" background={colors.peach10}>
        <FeaturesSectionTitle>
          <text.UppercaseOneLiner color={colors.darkPurple70}>
            Integrations
          </text.UppercaseOneLiner>
          <text.H2 color={colors.fadedPurple}>
            Make your other tools better
          </text.H2>
        </FeaturesSectionTitle>

        <FeatureBig>
          <IntegrationLeft>
            <Responsive.MobileOnly>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/mockup-extension.png'}
                alt={
                  "Toggl Track's browser extensions allow users to track time on 100+ online apps or using the extension in a browser's menu bar."
                }
                imgStyle={{ width: '100%', height: 'auto' }}
                placeholder="none"
                objectFit="contain"
              />
            </Responsive.MobileOnly>
            <IntegrationText>
              <text.H4 color={colors.fadedPurple}>
                <Link to="/track/integrations/">100+ integrations</Link> with
                our Chrome and Firefox browser extensions
              </text.H4>
              <text.P3 color={colors.darkPurple70}>
                Track time in tools that you already use. Installing the browser
                extension embeds the Toggl Track timer into the user interface
                of the online app so you can start the timer without switching
                tabs.
              </text.P3>
            </IntegrationText>{' '}
            <Link to="/track/integrations/">
              <text.UppercaseOneLiner color={colors.darkPurple70}>
                Popular integrations:
              </text.UppercaseOneLiner>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/popular-integrations.png'}
                alt={
                  'List of popular integrations Toggl Track integrates with such as Trello, Todoist, ClickUp, Asana, GitHub, Slack, and more.'
                }
                height={35}
                placeholder="none"
                objectFit="contain"
              />
            </Link>
          </IntegrationLeft>
          <TabImage>
            <StaticImage
              formats={['auto', 'webp', 'avif']}
              src={'./assets/mockup-extension.png'}
              alt={
                "Toggl Track's browser extensions allow users to track time on 100+ online apps or using the extension in a browser's menu bar."
              }
              height={570}
              placeholder="none"
              objectFit="contain"
              layout="fixed"
            />
          </TabImage>
        </FeatureBig>

        <FeaturesComponent
          id={links[4].id}
          data={features[4]}
          uploads={uploads.feature4}
        />
      </FeaturesSection>

      <SignUpDivider top={colors.peach10} bottom={colors.almostWhite}>
        <SignupFormFullWidth planId={0} textColor={colors.darkPurple} noDemo />
      </SignUpDivider>

      <FeaturesSection id="reporting" background={colors.almostWhite}>
        <FeaturesSectionTitle>
          <text.UppercaseOneLiner color={colors.darkPurple70}>
            Reporting
          </text.UppercaseOneLiner>
          <text.H2 color={colors.fadedPurple}>
            Get actionable insights from your data
          </text.H2>
        </FeaturesSectionTitle>
        <Reporting />

        <Responsive.MobileOnly>
          <FeaturesWrapper>
            <Card.FeatureIcon
              title="Summary, Detailed, and Weekly Reports"
              paragraph="Select the level of detail you want to see, filter and sort your data, and create client-ready visuals and reports exportable via CSV, PDF or Excel."
              image={
                <StaticImage
                  formats={['auto', 'webp', 'avif']}
                  src={'./assets/mockup-reports.png'}
                  alt={
                    "Toggl Track's summary reports with graphs and list of time entries sorted by projects and users"
                  }
                  imgStyle={{ width: '100%', height: 'auto' }}
                  placeholder="none"
                  objectFit="contain"
                />
              }
              icon
            />

            <Card.FeatureIcon
              title="Saved Reports"
              paragraph="Create a dynamic permalink for specific reports and share the link
            with clients so they can track your work live, or bookmark for easy
            access."
              image={
                <StaticImage
                  formats={['auto', 'webp', 'avif']}
                  src={'./assets/mockup-saved.png'}
                  alt={
                    "Toggl Track's saved reports option that allows public or private link sharing"
                  }
                  imgStyle={{ width: '100%', height: 'auto' }}
                  placeholder="none"
                  objectFit="contain"
                />
              }
              icon
            />
          </FeaturesWrapper>
        </Responsive.MobileOnly>

        <FeaturesComponent
          id={links[1].id}
          data={features[1]}
          uploads={uploads.feature1}
        />
      </FeaturesSection>

      <FeaturesSection id="project-management" background={colors.peach10}>
        <FeaturesSectionTitle>
          <text.UppercaseOneLiner color={colors.darkPurple70}>
            Project Management
          </text.UppercaseOneLiner>
          <text.H2 color={colors.fadedPurple}>
            Stay on track for all your projects
          </text.H2>
        </FeaturesSectionTitle>
        <ProjectManagement />

        <Responsive.MobileOnly>
          <FeaturesWrapper>
            <Card.FeatureIcon
              title="Project Dashboard"
              paragraph="Forecast timelines, budgets, and track progress as you go. Spot
            problems early and fight off scope creep with easy-to-read visual
            data."
              image={
                <StaticImage
                  formats={['auto', 'webp', 'avif']}
                  src={'./assets/mockup-project-dashboard.png'}
                  alt={
                    "Toggl Track's project dashboard showcasing project profitability based on billable rates"
                  }
                  imgStyle={{ width: '100%', height: 'auto' }}
                  placeholder="none"
                  objectFit="contain"
                />
              }
              icon
            />

            <Card.FeatureIcon
              title="Tasks (sub-projects)"
              paragraph="Add an additional layer of activity management below Projects,
            allowing teams to assign workloads and create precise reports."
              image={
                <StaticImage
                  formats={['auto', 'webp', 'avif']}
                  src={'./assets/mockup-tasks.png'}
                  alt={
                    'Toggl Track allows tasks, which adds another hierarchy of data for projects'
                  }
                  imgStyle={{ width: '100%', height: 'auto' }}
                  placeholder="none"
                  objectFit="contain"
                />
              }
              icon
            />
          </FeaturesWrapper>
        </Responsive.MobileOnly>

        <FeaturesComponent
          id={links[2].id}
          data={features[2]}
          uploads={uploads.feature2}
        />
      </FeaturesSection>

      <SectionBlock background={colors.darkPurple}>
        <StyledQuote>
          <text.H2 color={colors.pink}>
            Users <em>love</em> us!
            <br /> And we know you will too.
          </text.H2>
        </StyledQuote>

        <CaseStudies>
          <PersonaQuote>
            <text.P2 color={colors.almostWhite}>
              “Clients love the summaries, colleagues love the simplicity. We
              have used it for 7+ years and it is still going very well. The
              ease of use of Toggl Track is unprecedented.”
            </text.P2>
            <PersonaReview>
              <img
                src={require('./assets/icon-stars.svg').default}
                alt="Icon of 5 stars wow"
                loading="lazy"
              />
              5/5 stars on Capterra
            </PersonaReview>
            <text.P4 color={colors.darkPurple20}>
              — Jakub H, Founder of a design agency
            </text.P4>
          </PersonaQuote>
          <PersonaQuote>
            <text.P2 color={colors.almostWhite}>
              “In a weird way, Toggl Track kind of makes time tracking fun. It
              has a beautiful design that's easy to use, and a good accompanying
              mobile app as well.”
            </text.P2>
            <PersonaReview>
              <img
                src={require('./assets/icon-stars.svg').default}
                alt="Icon of 5 stars wow"
                loading="lazy"
              />
              5/5 stars on GetApp
            </PersonaReview>
            <text.P4 color={colors.darkPurple20}>
              — Sunny T, Marketing for an SMB
            </text.P4>
          </PersonaQuote>
          <PersonaQuote>
            <text.P2 color={colors.almostWhite}>
              “The acceptance from all users is very high. I have seen a 40+
              year employee move from their Excel-based tracking they have used
              forever, to this tool without any issue.”
            </text.P2>
            <PersonaReview>
              <img
                src={require('./assets/icon-stars.svg').default}
                alt="Icon of 5 stars wow"
                loading="lazy"
              />
              5/5 stars on G2.com
            </PersonaReview>
            <text.P4 color={colors.darkPurple20}>
              — Chris B, Software Lead in an enterprise
            </text.P4>
          </PersonaQuote>
          <PersonaQuote>
            <text.P2 color={colors.almostWhite}>
              “The app has a well-curated set of features that are
              context-flexible so as to not overly complicate the UX. The UI is
              beautiful and easy to navigate. Bottom line: I wish my company
              used Toggl Track.”
            </text.P2>
            <PersonaReview>
              <img
                src={require('./assets/icon-stars.svg').default}
                alt="Icon of 5 stars wow"
                loading="lazy"
              />
              5/5 stars on Google Play Store
            </PersonaReview>
          </PersonaQuote>
          <PersonaQuote>
            <text.P2 color={colors.almostWhite}>
              “It’s really good for me, because at the end of the day I can say
              how much time I spent” on each area of her life “and how it adds
              up”.
            </text.P2>
            <PersonaReview>
              On using Toggl Track during an interview with{' '}
              <a href="https://www.fastcompany.com/90581950/how-serena-williams-conquers-her-daily-schedule-without-breaking-a-sweat">
                FastCompany.com
              </a>
            </PersonaReview>
            <text.P4 color={colors.darkPurple20}>
              — Serena Williams, Tennis Icon
            </text.P4>
          </PersonaQuote>
          <PersonaQuote>
            <text.P2 color={colors.almostWhite}>
              “Toggl has changed the way my remote team operates and has helped
              give us back the most valuable resource — Time!”
            </text.P2>
            <PersonaReview>
              <img
                src={require('./assets/icon-stars.svg').default}
                alt="Icon of 5 stars wow"
                loading="lazy"
              />
              5/5 stars on Apple App Store
            </PersonaReview>
          </PersonaQuote>
        </CaseStudies>
        <CaseStudyCTA>
          <text.P2 color={colors.almostWhite}>
            No credit card required. All plans come with a free, 30-day trial of
            Toggl Track Premium. Upgrade at the end of the trial or continue
            using Track for free.
          </text.P2>
          <div>
            <Button.Primary as={Link} to="/track/signup/">
              Try Toggl Track for free
            </Button.Primary>
            <Button.Secondary
              as={Link}
              to="/track/demo-request/"
              color={colors.almostWhite}
              caretDirection="right"
            >
              Request a demo
            </Button.Secondary>
          </div>
        </CaseStudyCTA>
      </SectionBlock>

      <FeaturesSection id="team-management" background={colors.almostWhite}>
        <FeaturesSectionTitle>
          <text.UppercaseOneLiner color={colors.darkPurple70}>
            Team & Data Management
          </text.UppercaseOneLiner>
          <text.H2 color={colors.fadedPurple}>
            Get everyone on the same page
          </text.H2>
        </FeaturesSectionTitle>
        <TeamManagement />

        <Responsive.MobileOnly>
          <FeaturesWrapper>
            <Card.FeatureIcon
              title="User groups"
              paragraph="Assign team members to user groups to give the same project
            permissions to everyone at once."
              image={
                <StaticImage
                  formats={['auto', 'webp', 'avif']}
                  src={'./assets/mockup-user-groups.png'}
                  alt={
                    "Toggl Track's user group feature allows grouping of team members to simplify assigning billable rates, user costs, and tasks."
                  }
                  imgStyle={{ width: '100%', height: 'auto' }}
                  placeholder="none"
                  objectFit="contain"
                />
              }
              icon
            />

            <Card.FeatureIcon
              title="Team member audits"
              paragraph="Use filters to quickly find users who haven’t tracked time — or
            tracked too little or too much."
              image={
                <StaticImage
                  formats={['auto', 'webp', 'avif']}
                  src={'./assets/mockup-team-audits.png'}
                  alt={
                    'Toggl Track allows audits for reports, that helps you filter out suspicious or incorrect entries, such as time entries which are short than 30 seconds.'
                  }
                  imgStyle={{ width: '100%', height: 'auto' }}
                  placeholder="none"
                  objectFit="contain"
                />
              }
              icon
            />
          </FeaturesWrapper>
        </Responsive.MobileOnly>

        <FeaturesComponent
          id={links[3].id}
          data={features[3]}
          uploads={uploads.feature3}
        />
        <ContactSales>
          <ContactSalesCTA>
            <text.H3Small color={colors.fadedPurple}>
              Onboarding a large team? We’d love to show you around.
            </text.H3Small>
            <text.P2 color={colors.darkPurple70}>
              Teams of 10+ are eligible for a custom demo to see how Toggl Track
              works at scale.
            </text.P2>
          </ContactSalesCTA>

          <Button.Primary as={Link} to="/track/demo-request/">
            Book a demo
          </Button.Primary>
          <hr />
          {ContactSalesPoints.map((contactSalesPoint, index) => (
            <SalesPoint key={index}>
              <img
                src={require('./assets/icon-checkmark.png').default}
                alt="Checkmark icon"
                loading="lazy"
              />
              <text.P3 color={colors.fadedPurple}>
                {contactSalesPoint.text}
              </text.P3>
            </SalesPoint>
          ))}
        </ContactSales>
      </FeaturesSection>

      <TrackDownloads />

      <SectionMobileNoBleedBlock>
        <StyledSignup>
          <text.H2>
            Let us help you get on <em>Track</em>.
          </text.H2>
          <Signup demoSource="features" />
        </StyledSignup>
      </SectionMobileNoBleedBlock>
    </LayoutTrack>
  )
}

const SplitContent = styled.div`
  ${media.mq[1]} {
    display: flex;
    gap: 50px;
    align-items: center;
  }
`

const LeftContent = styled.div`
  ${media.mq[1]} {
    flex: 1.5;
  }

  h1 {
    margin-bottom: 15px;
  }
  p {
    margin: 20px 0 30px;
    max-width: 570px;
  }
  a {
    display: inline-flex;
  }
`

const RightContent = styled.div`
${media.mq[1]} {
  flex: 1.2;
}

position: relative;
button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 16px 20px 0 rgba(0, 0, 0, 0.25);
  z-index: 1000;
  transition: 0.25s ease;
  &:hover {
    box-shadow: 0 50px 80px 0 rgba(0, 0, 0, 0.6);
    transform: translate(-50%, -55%);
}
`

const StyledSectionModalVideo = styled(SectionModalVideo)`
  > div > div {
    display: inline-block;
    max-width: 900px;
    margin-top: 30px;
    ${media.mq[1]} {
      margin: -20px auto 0;
    }
    
  }
  .gatsby-image-wrapper {
    max-width: 100%;
  }
}
`

const Navigation = styled.div`
  background: ${colors.fadedPurple};
`
const NavigationWrapper = styled.div`
  max-width: 1140px;
  margin: auto;
  p {
    margin: 20px 20px 15px;
    text-align: center;
  }
  ${media.mq[2]} {
    display: flex;
    align-items: center;
    justify-content: center;
    p {
      margin-bottom: 20px;
    }
  }
`

const BunchOfLinks = styled.div`
  overflow-x: auto;
  display: flex;
  padding: 0 20px 15px;
  ${media.mq[1]} {
    justify-content: center;
  }
  ${media.mq[2]} {
    padding-top: 15px;
  }
  a {
    ${text.defaultText};
    border: none;
    border-radius: 100px;
    background: ${(p) => (p.active ? colors.fadedPurple : colors.yellow60)};
    color: ${(p) => (p.active ? colors.yellow : colors.fadedPurple)};
    ${(p) => p.active && `font-family: ${fonts.gtHaptikMediumRotalic}`};
    padding: 13px 20px;
    cursor: pointer;
    min-width: fit-content;
    &:hover {
      background: ${(p) => (p.active ? colors.fadedPurple : colors.yellow80)};
      font-family: ${fonts.gtHaptikMediumRotalic};
    }
    &:not(last-of-type) {
      margin-right: 10px;
    }
  }
`

const VideoButton = styled(Button.Primary)`
  background-color: ${colors.darkPurple};
  justify-content: center;
  width: 240px;
`

const StyledSignup = styled.div`
  h2 {
    text-align: center;
    em {
      color: ${(p) => p.theme.primary};
    }
    margin-bottom: 40px;
    padding-top: 20px;
  }

  ${media.mq[1]} {
    h2 {
      margin-bottom: 40px;
    }
  }
`

const CaseStudyCTA = styled.div`
  margin: 50px auto 10px;
  text-align: center;
  max-width: 700px;
  div {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 30px;
    margin-top: 30px;
  }
  a {
    display: inline-flex;
  }
`

const StyledQuote = styled.div`
  text-align: center;
  margin-bottom: 60px;
`

const FeaturesSection = styled(SectionBlock)`
  overflow-x: hidden;
`

const FeaturesSectionTitle = styled.div`
  text-align: center;
  p {
    margin-top: 30px;
  }
  h2 {
    margin: 15px 0 30px;
  }
`

const FeatureBig = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${media.mq[1]} {
    flex-direction: row;
    gap: 15px;
  }
  ${media.mq[2]} {
    gap: 70px;
  }
  h5 {
    line-height: 1.2;
    margin-bottom: 8px;
  }
`

const IntegrationLeft = styled.div`
  ${media.mq[1]} {
    flex: 0.65;
  }
  > a {
    display: block;
    margin: 30px 0;
    ${media.mq[1]} {
      margin-top: 45px;
    }
  }
  p {
    margin-bottom: 15px;
  }
  > div:nth-of-type(1) {
    margin-bottom: 30px;
    ${media.mq[1]} {
      margin-bottom: 40px;
    }
  }
  h4 {
    ${text.heading5};
    line-height: 1.2;
    margin-bottom: 8px;
  }
`

const IntegrationText = styled.div`
  a {
    color: ${colors.fadedPurpleAlt};
    text-decoration: underline;
    &:hover {
      color: ${colors.pink};
    }
  }
`

const Tabs = styled.div`
  display: none;
  ${media.mq[1]} {
    display: block;
    flex: 0.65;
    margin: 15px 0;
  }
`

const Tab = styled.button`
  min-width: fit-content;
  border: none;
  padding: 20px 20px 20px 25px;
  border-radius: 10px;
  margin: 2px 0;
  cursor: pointer;
  text-align: left;
  color: ${(p) => (p.active ? colors.yellow : colors.fadedPurple)};
  background: ${(p) =>
    p.active ? `${colors.fadedPurple} !important` : colors.peach80};
  h5 {
    color: ${(p) => (p.active ? colors.pink : colors.fadedPurple)};
    ${(p) => p.active && `font-family: ${fonts.gtHaptikMediumRotalic}`};
  }
  p {
    color: ${(p) => (p.active ? colors.almostWhite : colors.darkPurple70)};
  }
  &:hover {
    background: ${colors.beige};
    h5 {
      font-family: ${fonts.gtHaptikMediumRotalic};
    }
  }
`

const TabImage = styled.div`
  flex: 1;
  text-align: center;
  position: relative;
  display: none;
  ${media.mq[1]} {
    min-height: 620px;
    display: block;
    .gatsby-image-wrapper {
      position: absolute;
      top: 0;
      bottom: 0;
      margin: auto;
      left: 0;
    }
  }
`

const FeaturesWrapper = styled.div`
  max-width: 1140px;
  margin: 30px auto 0;
  ${media.mq[1]} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 40px;
    row-gap: 20px;
  }
  ${media.mq[2]} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const SignUpDivider = styled(SectionIceCream)`
  padding: 0 !important;
`

const ContactSales = styled.div`
  background: ${colors.beige};
  border-radius: 10px;
  padding: 20px 30px;
  display: grid;
  grid-template-columns: 100%;
  grid-row-gap: 20px;
  ${media.mq[1]} {
    display: grid;
    align-items: center;
    grid-column-gap: 20px;
    grid-template-columns: repeat(8, 1fr);
    padding: 30px 35px;
    margin-top: 30px;
  }
  a {
    display: inline-flex;
    justify-content: center;
    text-align: center;
    ${media.mq[1]} {
      grid-column: span 3;
    }
    ${media.mq[2]} {
      grid-column: span 2;
    }
  }
  hr {
    grid-column: 1 / -1;
    width: 100%;
    border: 1.5px solid ${colors.pink50};
  }
`

const ContactSalesCTA = styled.div`
  ${media.mq[1]} {
    grid-column: 1 / 6;
  }
  ${media.mq[2]} {
    grid-column: 1 / 7;
  }
  h3 {
    margin-bottom: 8px;
  }
`

const SalesPoint = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  ${text.paragraph3};
  color: ${colors.fadedPurple};
  ${media.mq[1]} {
    grid-column: span 4;
  }
  ${media.mq[2]} {
    grid-column: span 2;
  }
  p {
    line-height: 1.35;
  }
  img {
    width: 35px;
  }
`

const CaseStudies = styled.div`
  display: grid;
  grid-gap: 25px;
  ${media.mq[1]} {
    grid-template-columns: repeat(3, 1fr);
    margin-bottom: 40px;
  }
`

const PersonaQuote = styled.div`
  background: rgba(64, 42, 76, 0.5);
  padding: 35px;
  border-radius: 10px;
  p:first-of-type {
    margin-bottom: 25px;
  }
  ${media.mq[1]} {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`

const PersonaReview = styled.div`
  ${text.paragraph5};
  color: ${colors.darkPurple20};
  font-weight: bold;
  margin-top: auto;
  img {
    display: inline-block;
    width: 90px;
    margin: -1px 8px 0 0;
    vertical-align: middle;
  }
`

export default FeaturesPage
